import React, { useState } from "react";
import { IconButton } from "@material-ui/core";
import { Field, FieldArray, useFormikContext } from "formik";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";

import ReactSelect, { components, StylesConfig } from "react-select";
import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";
import { useFieldResourceStyles } from "../useFieldResourceStyles";

import {
  CaseDataResponse,
  FieldResourceInformation,
  FieldResourceOption,
} from "../../../../types/CaseTypes";
import FMUInputField from "../../../shared/FormIkMUFormComponent/FMUInputField";
import { DeleteIconButton } from "../../../shared/helpers/GlobalStylesv5";
import { useCaseViewStyle } from "../../useCaseViewStyle";
import { Typography } from "@mui/material";
import { tokens } from "../../../../theme/tokens";

interface OptionData {
  label: string;
  value: string;
  Icon?: React.ComponentType;
}

export const selectStyle: StylesConfig<OptionData, false> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${
      state.isFocused ? tokens.color.focus.ring : tokens.color.border.default
    }`,
    borderRadius: tokens.radius.sm,
    minHeight: "36px",
    height: "36px",
    padding: "0 8px",
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: tokens.color.surface.default,
      border: `${tokens.borderWidth} solid ${tokens.color.border.strong}`,
    },
  }),
  container: (provided) => ({
    ...provided,
    width: "100%",
    textAlign: "left",
    zIndex: 999,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
    height: "30px",
  }),
  input: (provided) => ({
    ...provided,
    textAlign: "left",
    margin: "0",
    padding: "0",
    color: tokens.color.text.primary,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: tokens.color.text.primary,
    fontSize: "1em",
    margin: "0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: tokens.radius.sm,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    boxShadow: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? tokens.color.brand.primaryAction
      : state.isFocused
      ? tokens.color.surface.hover
      : tokens.color.surface.default,
    color: state.isSelected
      ? tokens.color.brand.onPrimary
      : tokens.color.text.primary,
    padding: "8px 12px",
    cursor: "pointer",
    "&:active": {
      backgroundColor: tokens.color.surface.hoverStrong,
    },
  }),
};

const IconOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          alignSelf: "center",
        }}
      >
        <span>{props.data.label}</span>
        {!props.data.isNone && (
          <>
            <StarIcon style={{ marginLeft: "10px" }} />
            <span>{Math.round(props.data.averageRating * 100) / 100}</span>
          </>
        )}
      </div>
    </components.Option>
  );
};

interface FieldResourceCardProps {
  fieldResource: FieldResourceInformation;
  index: number;
  fieldResourceList: FieldResourceOption[];
}

const FieldResourceCard: React.FC<FieldResourceCardProps> = ({
  fieldResource,
  index,
  fieldResourceList,
}) => {
  const classes = useFieldResourceStyles();
  const caseClasses = useCaseViewStyle();
  const { setFieldValue } = useFormikContext<CaseDataResponse>();
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const safeParseFloat = (value: string | number): number => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  return (
    <div className={classes.leftInformationDiv}>
      <div className={classes.infoContainer}>
        {/* Field Resource Name */}
        <div className={classes.cardContentContainer}>
          <div className={classes.labelContainer}>
            <Typography variant="h6" className={classes.label}>
              Field Resource Name
            </Typography>
          </div>
          <ReactSelect
            value={fieldResourceList.find(
              (option) => option.value === fieldResource?.id
            )}
            styles={selectStyle}
            onChange={(e) => {
              setFieldValue(
                `case_detail.case_details.field_resource_information[${index}].id`,
                e?.value || "",
                false
              );
            }}
            options={fieldResourceList}
            formatOptionLabel={(option: any) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignSelf: "center",
                }}
              >
                <span>{option.label}</span>
                {!option.isNone && (
                  <>
                    <StarIcon style={{ marginLeft: "10px" }} />
                    <span>{Math.round(option.averageRating * 100) / 100}</span>
                  </>
                )}
              </div>
            )}
            components={{ Option: IconOption }}
          />
        </div>

        {/* Date Hired */}
        <div className={classes.cardContentContainer}>
          <div className={classes.labelContainer}>
            <Typography variant="h6" className={classes.label}>
              Date hired
            </Typography>
          </div>
          <Field
            name={`case_detail.case_details.field_resource_information[${index}].date_hired`}
            className={caseClasses.formikInput}
            type="date"
            disableUnderline={true}
            component={FMUInputField}
          />
        </div>

        {/* Due Date */}
        <div className={classes.cardContentContainer}>
          <div className={classes.labelContainer}>
            <Typography variant="h6" className={classes.label}>
              Due Date
            </Typography>
          </div>
          <Field
            name={`case_detail.case_details.field_resource_information[${index}].due_date`}
            className={caseClasses.formikInput}
            type="date"
            disableUnderline={true}
            component={FMUInputField}
          />
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div
            style={{
              maxHeight: "700px",
              transition: "max-height 1s ease-in-out",
            }}
          >
            {/* Hours Authorized */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Hours authorized
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].hours_authorized`}
                className={caseClasses.formikInput}
                type="number"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Hours Billed */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Hours billed
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].hours_billed`}
                className={caseClasses.formikInput}
                type="number"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Miles Billed */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Miles billed
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].miles_billed`}
                className={caseClasses.formikInput}
                type="number"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Database Billed */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Database billed
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].database_billed`}
                className={caseClasses.formikInput}
                type="number"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Report Received Date */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Report received date
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].report_received_date`}
                className={caseClasses.formikInput}
                type="date"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Invoice Received Date */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Invoice received date
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].invoice_received_date`}
                className={caseClasses.formikInput}
                type="date"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Invoice Approved Date */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Invoice approved date
                </Typography>
              </div>
              <Field
                name={`case_detail.case_details.field_resource_information[${index}].invoice_approved_date`}
                className={caseClasses.formikInput}
                type="date"
                disableUnderline={true}
                component={FMUInputField}
              />
            </div>

            {/* Rating */}
            <div className={classes.cardContentContainer}>
              <div className={classes.labelContainer}>
                <Typography variant="h6" className={classes.label}>
                  Rating
                </Typography>
              </div>
              <Rating
                name={`rating-${index}`}
                value={safeParseFloat(fieldResource.rating)}
                defaultValue={2.5}
                precision={0.5}
                // onChange={(event, rating) => {
                //   setFieldValue(
                //     `case_detail.case_details.field_resource_information[${index}].rating`,
                //     rating || 0,
                //     false
                //   );
                // }}
                onChange={(event, rating) => {
                  // Convert rating to string to avoid DynamoDB float issues
                  setFieldValue(
                    `case_detail.case_details.field_resource_information[${index}].rating`,
                    rating !== null ? String(rating) : "0",
                    false
                  );
                }}
                sx={{
                  fontSize: "1.8rem",
                }}
                style={{ marginTop: "3px" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className={classes.bottomButtonRow}>
        <FieldArray name="case_detail.case_details.field_resource_information">
          {({ remove }) => (
            // <IconButton
            //   className={classes.removeIconButton}
            //   onClick={() => remove(index)}
            // >
            //   <DeleteIcon />
            // </IconButton>
            <span className={classes.removeIconButton}>
              <DeleteIconButton onClick={() => remove(index)} />
            </span>
          )}
        </FieldArray>
        <IconButton
          className={classes.expandIconButton}
          onClick={handleExpandClick}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
    </div>
  );
};

export default FieldResourceCard;
